import Divider from '@material-ui/core/Divider'
import classNames from 'classnames'
import inRange from 'lodash/inRange'
import { useObserver } from 'mobx-react-lite'
import React, { memo, useContext } from 'react'
import { FC } from 'react'
import RendererContext from '../../contexts/renderer-context'
import useScrollRange from '../../hooks/use-scroll-range'
import { Column } from '../../models/column'
import Cell from './Cell'
import { productConfiguration } from '@stores/productConfigurationStore'
import { makeStyles } from '@material-ui/core'
import { stores } from '@stores'
import { View } from '@modules/order/constants/views'
import { KPITableView } from '@modules/order/stores/view/KPI/EnhancedKPIViewTableService'

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    height: 'fit-content',
    backgroundColor: 'white'
  },
  containerBorder: {
    borderRight: '1px solid #ededed'
  },
  border: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    zIndex: 1000
  },
  subColumnsContainer: {
    display: 'flex'
  },
  lastDoorCellWithBorder: {
    '& .commonBodyCellOverride:last-child': {
      borderRight: '1px solid #eeeeee'
    }
  }
}))

export interface HierarchyRowProps {
  columns: Column[]
  rowData: any
  id: string
  showBorders: boolean
  enhancedViewCell?: boolean
}

interface PrivateRowProps {
  stickyColsPosition: { [field: string]: number }
}

const HierarchyRowRecursive: FC<
  Omit<HierarchyRowProps, 'showBorders'> & PrivateRowProps & { offset: number }
> = memo(({ columns, id, rowData, stickyColsPosition, offset, enhancedViewCell }) =>
  useObserver(function useHook() {
    const {
      tableContext: {
        tableStore: { priorColumnOffsetWidthByColumnField }
      }
    } = useContext(RendererContext)
    const [left, right] = useScrollRange()
    const {
      productConfiguration: { enhancedViewEnabled },
      nav: {
        queryParams: { view, subSection }
      }
    } = stores
    const classes = useStyles()
    return (
      <>
        {columns?.map((column, index) => {
          return column?.columns?.length ? (
            <div
              className={classNames(classes.subColumnsContainer, {
                [classes.lastDoorCellWithBorder]:
                  enhancedViewEnabled &&
                  view === View.KPI &&
                  subSection === KPITableView.DoorsKPI
              })}
            >
              <HierarchyRowRecursive
                columns={column.columns}
                id={id}
                rowData={rowData}
                key={column.field}
                stickyColsPosition={stickyColsPosition}
                offset={priorColumnOffsetWidthByColumnField(column.field)}
              />
            </div>
          ) : inRange(priorColumnOffsetWidthByColumnField(column.field), left, right) ||
            column.fixed ||
            column.autoHeight ? (
            <Cell
              offset={priorColumnOffsetWidthByColumnField(column.field)}
              id={id}
              enhancedViewCell={enhancedViewCell}
              key={column.field}
              column={column}
              rowData={rowData}
              stickyPosition={stickyColsPosition[column.field]}
            />
          ) : (
            <div
              style={{
                width: column.width,
                left: priorColumnOffsetWidthByColumnField(column.field)
              }}
            />
          )
        })}
      </>
    )
  })
)

const HierarchyRow: FC<HierarchyRowProps & PrivateRowProps> = props => {
  const { enhancedViewEnabled } = productConfiguration
  const classes = useStyles()
  return (
    <div
      className={classNames(classes.container, {
        [classes.containerBorder]: props.showBorders && !enhancedViewEnabled
      })}
    >
      <HierarchyRowRecursive {...props} offset={0} />
      {props.showBorders && <Divider variant='fullWidth' className={classes.border} />}
    </div>
  )
}

export default HierarchyRow
